import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DatabaseViewLekResponse, DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { CATEGORIES_API } from '../../../api/injection-tokens/services.token';
import { provideCategoryApi } from '../../../api/services/categories.service';
import { DATABASE_VIEW_API, provideDatabaseViewApi } from '../../../api/services/database-view.service';
import { DISABILITY_TYPE_SERVICE, provideDisabilityTypeApi } from '../../../api/services/disability-type.api';
import { INTEGRATION_OFFICE_SERVICE, provideIsoApi } from '../../../api/services/integration-office.service';
import { LEGAL_FOUNDATION_SERVICE, provideLegalFoundationApi } from '../../../api/services/legal-foundation.service';
import { provideUserApi, USER_SERVICE } from '../../../api/services/user.service';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { Paginator } from '../../../core/components/paginator/paginator';
import { ERROR_PROVIDER } from '../../../core/services/error.service';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { TENANT_FEATURES } from '../../../core/services/tenant-feature.service';
import { formatDateToISOString, matDateProviders, parseStoredDay } from '../../../shared/helpers/date.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { DatabaseColumnSelector } from '../shared/components/database-column-selector/database-column-selector';
import {
	DatabaseDateRangeFilter,
	DatabaseDateRangeValue,
} from '../shared/components/database-date-range-filter/database-date-range-filter';
import { DatabaseReferenceDateFilter } from '../shared/components/database-single-date-filter/database-reference-date-filter';
import { DatabaseFilterPanelRegistry } from '../shared/components/database-multi-select-filter/database-filter-panel.registry';
import { DatabaseMultiSelectFilter } from '../shared/components/database-multi-select-filter/database-multi-select-filter';
import { DatabaseColumnOption } from '../shared/models/database-column-selection.types';
import { DatabaseSortDirection } from '../shared/models/database-list.types';
import { DatabaseFilterOption } from '../shared/models/database-view-storage.types';
import { DatabaseDateFormatService } from '../shared/services/database-date-format.service';
import { DatabaseViewStorage } from '../shared/services/database-view-storage.service';
import {
	areColumnIdsEqual,
	reconcileColumnIds,
	resetStateForRemovedColumns,
} from '../shared/utils/database-column-selection';
import { runDatabaseCsvExport } from '../shared/utils/database-csv-export';
import { isReferenceDateAvailable, REFERENCE_DATE_COLUMN_IDS } from '../shared/utils/database-reference-date';
import { DATABASE_PHASE_FILTER_OPTIONS } from '../shared/utils/database-filter-options';
import {
	DATABASE_FILTER_SOURCE_KEYS,
	DatabaseFilterSourceKey,
	loadDatabaseFilterSourceOptions,
	nameOptions,
} from '../shared/utils/database-filter-sources';
import {
	createRecordFromKeys,
	filterOptionLabels,
	filterOptionValues,
	isNumericFilterId,
	sanitizeFilterOptionEntries,
	toggleFilterSelection,
} from '../shared/utils/database-filter-state';
import {
	DatabaseRangePreset,
	isDatabaseRangePreset,
	resolveDatabaseRangePreset,
} from '../shared/utils/database-range-preset';
import {
	buildDatabaseViewLekRequest,
	LEK_DATE_RANGE_FIELD_IDS,
	LekDateRangeFieldId,
	LekListFilterKey,
	LekListSortField,
} from './database-lek-list-request';

type FilterKey = LekListFilterKey;
type LekSortField = LekListSortField;
type ColumnKey =
	| 'assistanceResult'
	| 'clientName'
	| 'startDate'
	| 'endDate'
	| 'duration'
	| 'categorySponsor'
	| 'phase'
	| 'typeOfCare'
	| 'startOfCare'
	| 'cancellationDate'
	| 'caseGroup'
	| 'legalFoundation'
	| 'initiator'
	| 'ifd'
	| 'service'
	| 'advisor'
	| 'disabilityType'
	| 'assignmentNumber'
	| 'institutionAtStart'
	| 'institutionAtDate'
	| 'institutionAtEnd'
	| 'schoolNumberAtStart'
	| 'schoolNumberAtDate'
	| 'schoolNumberAtEnd'
	| 'socialSecurityStatusAtStart'
	| 'socialSecurityStatusAtDate'
	| 'socialSecurityStatusAtEnd'
	| 'disabilityTypeAtStart'
	| 'disabilityTypeAtDate'
	| 'disabilityTypeAtEnd'
	| 'disabilityProofAtStart'
	| 'disabilityProofAtDate'
	| 'disabilityProofAtEnd'
	| 'gdbAtStart'
	| 'gdbAtDate'
	| 'gdbAtEnd'
	| 'assignmentSponsor'
	| 'lastThreeContactDates'
	| 'contactTaskCount'
	| 'impedimentsAtStart'
	| 'impedimentsAtEnd';

interface LekRow {
	clientId: number;
	assignmentId: number;
	ifdId: number;
	advisorId: number;
	advisorGuid: string;
	assistanceResult: string;
	clientName: string;
	startDate: string;
	endDate: string;
	duration: string;
	categorySponsor: string;
	phase: string;
	typeOfCare: string;
	startOfCare: string;
	cancellationDate: string;
	caseGroup: string;
	legalFoundation: string;
	initiator: string;
	ifd: string;
	service: string;
	advisor: string;
	disabilityType: string;
	assignmentNumber: string;
	institutionAtStart: string;
	institutionAtDate: string;
	institutionAtEnd: string;
	schoolNumberAtStart: string;
	schoolNumberAtDate: string;
	schoolNumberAtEnd: string;
	socialSecurityStatusAtStart: string;
	socialSecurityStatusAtDate: string;
	socialSecurityStatusAtEnd: string;
	disabilityTypeAtStart: string;
	disabilityTypeAtDate: string;
	disabilityTypeAtEnd: string;
	disabilityProofAtStart: string;
	disabilityProofAtDate: string;
	disabilityProofAtEnd: string;
	gdbAtStart: string;
	gdbAtDate: string;
	gdbAtEnd: string;
	assignmentSponsor: string;
	lastThreeContactDates: string;
	contactTaskCount: string;
	impedimentsAtStart: string;
	impedimentsAtEnd: string;
}

interface LekColumn {
	key: ColumnKey;
	labelKey: string;
	filterKey?: FilterKey;
	filterType?: 'search' | 'combo' | 'dateRange';
	fieldId?: LekDateRangeFieldId;
	sortField?: LekSortField;
}

const LEK_SCHEMA_IDS: Record<ColumnKey, string> = {
	assistanceResult: 'assistanceResult',
	clientName: 'clientName',
	startDate: 'startDate',
	endDate: 'endDate',
	duration: 'duration',
	categorySponsor: 'categorySponsor',
	phase: 'phase',
	typeOfCare: 'typeOfCare',
	startOfCare: 'startOfCare',
	cancellationDate: 'cancellationDate',
	caseGroup: 'caseGroup',
	legalFoundation: 'legalFoundation',
	initiator: 'initiator',
	ifd: 'integrationServiceOffice',
	service: 'service',
	advisor: 'advisorName',
	disabilityType: 'disabilityType',
	assignmentNumber: 'assignmentNumber',
	institutionAtStart: 'institutionAtStart',
	institutionAtDate: 'institutionAtDate',
	institutionAtEnd: 'institutionAtEnd',
	schoolNumberAtStart: 'schoolNumberAtStart',
	schoolNumberAtDate: 'schoolNumberAtDate',
	schoolNumberAtEnd: 'schoolNumberAtEnd',
	socialSecurityStatusAtStart: 'socialSecurityStatusAtStart',
	socialSecurityStatusAtDate: 'socialSecurityStatusAtDate',
	socialSecurityStatusAtEnd: 'socialSecurityStatusAtEnd',
	disabilityTypeAtStart: 'disabilityTypeAtStart',
	disabilityTypeAtDate: 'disabilityTypeAtDate',
	disabilityTypeAtEnd: 'disabilityTypeAtEnd',
	disabilityProofAtStart: 'disabilityProofAtStart',
	disabilityProofAtDate: 'disabilityProofAtDate',
	disabilityProofAtEnd: 'disabilityProofAtEnd',
	gdbAtStart: 'gdbAtStart',
	gdbAtDate: 'gdbAtDate',
	gdbAtEnd: 'gdbAtEnd',
	assignmentSponsor: 'assignmentSponsor',
	lastThreeContactDates: 'lastThreeContactDates',
	contactTaskCount: 'contactTaskCount',
	impedimentsAtStart: 'impedimentsAtStart',
	impedimentsAtEnd: 'impedimentsAtEnd',
};

const FILTER_KEYS: FilterKey[] = [
	'assistanceResult',
	'clientName',
	'categorySponsor',
	'phase',
	'typeOfCare',
	'caseGroup',
	'legalFoundation',
	'initiator',
	'integrationServiceOffice',
	'service',
	'advisorName',
	'disabilityType',
];
const SEARCH_FILTER_KEYS: FilterKey[] = ['clientName'];
const COMBO_FILTER_KEYS: FilterKey[] = FILTER_KEYS.filter((key) => !SEARCH_FILTER_KEYS.includes(key));
const EMPTY_FILTER_VALUES = createRecordFromKeys(FILTER_KEYS, () => [] as string[]);
const EMPTY_FILTER_SEARCH = createRecordFromKeys(FILTER_KEYS, () => '');
const EMPTY_DATE_RANGE: DatabaseDateRangeValue = { start: null, end: null };
const EMPTY_FILTER_OPTIONS = createRecordFromKeys(FILTER_KEYS, () => [] as DatabaseFilterOption[]);
const LEK_PRELOAD_FILTER_KEYS: FilterKey[] = [
	'assistanceResult',
	'categorySponsor',
	'typeOfCare',
	'caseGroup',
	'legalFoundation',
	'initiator',
	'integrationServiceOffice',
	'service',
	'disabilityType',
	'advisorName',
];
const TAB_ID = 'leks';
const SCHEMA_VIEW = 'assistance-results';
type LekSharedFilterSourceKey = Exclude<DatabaseFilterSourceKey, 'assignmentSponsor'>;
const LEK_FILTER_SOURCE_KEYS = [
	...DATABASE_FILTER_SOURCE_KEYS.filter((key): key is LekSharedFilterSourceKey => key !== 'assignmentSponsor'),
	'categorySponsor',
	'assistanceResult',
] as const;
type LekFilterSourceKey = (typeof LEK_FILTER_SOURCE_KEYS)[number];

interface LekStoredState {
	pageIndex?: number;
	sortField?: LekSortField | null;
	sortDirection?: DatabaseSortDirection;
	rangeStart?: string | null;
	rangeEnd?: string | null;
	rangePreset?: string | null;
	filterValues?: Partial<Record<FilterKey, string[]>>;
	filterSearch?: Partial<Record<FilterKey, string>>;
	selectedColumns?: string[];
	dateRanges?: Partial<Record<string, { start: string | null; end: string | null }>>;
	referenceDate?: string | null;
}

const parseLekStoredState = (raw: unknown): LekStoredState | undefined =>
	typeof raw === 'object' && raw !== null ? (raw as LekStoredState) : undefined;

const RANGE_PRESET_LABEL_KEYS: Record<DatabaseRangePreset, string> = {
	lastMonth: 'database_view.range_presets.last_month',
	lastQuarter: 'database_view.range_presets.last_quarter',
	lastYear: 'database_view.range_presets.last_year',
};
const RANGE_PRESET_ICONS: Record<DatabaseRangePreset, string> = {
	lastMonth: 'calendar_view_month',
	lastQuarter: 'date_range',
	lastYear: 'calendar_today',
};
const RANGE_PRESET_PLACEHOLDER_KEY = 'database_view.range_presets.placeholder';
const PAGINATOR_PREFERENCE_KEY = 'database-lek-list';

@Component({
	selector: 'lvr-database-lek-list',
	templateUrl: './database-lek-list.html',
	styleUrl: './database-lek-list.scss',
	providers: [
		provideCategoryApi,
		provideLegalFoundationApi,
		provideUserApi,
		provideDisabilityTypeApi,
		provideIsoApi,
		provideDatabaseViewApi,
		matDateProviders,
	],
	imports: [
		MatIcon,
		MatIconButton,
		MatMenuModule,
		MatProgressBarModule,
		MatTableModule,
		MatTooltipModule,
		MatFormFieldModule,
		MatDatepickerModule,
		TranslocoPipe,
		Paginator,
		RouterLink,
		LvrButton,
		DatabaseColumnSelector,
		DatabaseDateRangeFilter,
		DatabaseMultiSelectFilter,
		DatabaseReferenceDateFilter,
	],
})
export class DatabaseLekList {
	private readonly databaseViewApi = inject(DATABASE_VIEW_API);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly viewStorage = inject(DatabaseViewStorage);
	private readonly dateFormat = inject(DatabaseDateFormatService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly errorService = inject(ERROR_PROVIDER);
	private readonly transloco = inject(TranslocoService);
	private readonly environment = inject(ENVIRONMENT);
	private readonly categoriesApi = inject(CATEGORIES_API);
	private readonly legalFoundationApi = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly disabilityTypeApi = inject(DISABILITY_TYPE_SERVICE);
	private readonly isoService = inject(INTEGRATION_OFFICE_SERVICE);
	private readonly userService = inject(USER_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly filterPanels = inject(DatabaseFilterPanelRegistry);
	private readonly destroyRef = inject(DestroyRef);
	private readonly representation = inject(REPRESENTATION_MODE, { optional: true });

	private readonly selector = viewChild(DatabaseColumnSelector);

	private readonly filterOptionsRange = this.environment.pagination.range;
	private readonly filterOptionsOffset = this.environment.pagination.offset;
	public readonly rangePresets = Object.keys(RANGE_PRESET_LABEL_KEYS) as DatabaseRangePreset[];
	public readonly rangePresetLabelKeys = RANGE_PRESET_LABEL_KEYS;
	public readonly rangePresetIcons = RANGE_PRESET_ICONS;
	public readonly rangePresetPlaceholderKey = RANGE_PRESET_PLACEHOLDER_KEY;
	private readonly allColumns: LekColumn[] = [
		{
			key: 'assistanceResult',
			labelKey: 'database_view.leks.columns.assistance_result',
			filterKey: 'assistanceResult',
			filterType: 'combo',
			sortField: 'assistanceResult',
		},
		{
			key: 'clientName',
			labelKey: 'database_view.leks.columns.client',
			filterKey: 'clientName',
			filterType: 'search',
			sortField: 'clientName',
		},
		{ key: 'startDate', labelKey: 'database_view.leks.columns.start_date' },
		{ key: 'endDate', labelKey: 'database_view.leks.columns.end_date' },
		{ key: 'duration', labelKey: 'database_view.leks.columns.duration' },
		{
			key: 'categorySponsor',
			labelKey: 'database_view.leks.columns.category_sponsor',
			filterKey: 'categorySponsor',
			filterType: 'combo',
			sortField: 'categorySponsor',
		},
		{
			key: 'phase',
			labelKey: 'database_view.leks.columns.phase',
			filterKey: 'phase',
			filterType: 'combo',
			sortField: 'phase',
		},
		{
			key: 'typeOfCare',
			labelKey: 'database_view.leks.columns.type_of_care',
			filterKey: 'typeOfCare',
			filterType: 'combo',
			sortField: 'typeOfCare',
		},
		{
			key: 'startOfCare',
			labelKey: 'database_view.leks.columns.start_of_care',
			filterType: 'dateRange',
			fieldId: 'startOfCare',
		},
		{
			key: 'cancellationDate',
			labelKey: 'database_view.leks.columns.cancellation_date',
			filterType: 'dateRange',
			fieldId: 'cancellationDate',
		},
		{
			key: 'caseGroup',
			labelKey: 'database_view.leks.columns.case_group',
			filterKey: 'caseGroup',
			filterType: 'combo',
			sortField: 'caseGroup',
		},
		{ key: 'assignmentSponsor', labelKey: 'database_view.leks.columns.assignment_sponsor' },
		{
			key: 'legalFoundation',
			labelKey: 'database_view.leks.columns.legal_foundation',
			filterKey: 'legalFoundation',
			filterType: 'combo',
			sortField: 'legalFoundation',
		},
		{
			key: 'initiator',
			labelKey: 'database_view.leks.columns.initiator',
			filterKey: 'initiator',
			filterType: 'combo',
			sortField: 'initiator',
		},
		{
			key: 'ifd',
			labelKey: 'database_view.leks.columns.ifd',
			filterKey: 'integrationServiceOffice',
			filterType: 'combo',
			sortField: 'integrationServiceOffice',
		},
		{
			key: 'service',
			labelKey: 'database_view.leks.columns.service',
			filterKey: 'service',
			filterType: 'combo',
			sortField: 'service',
		},
		{
			key: 'advisor',
			labelKey: 'database_view.leks.columns.advisor',
			filterKey: 'advisorName',
			filterType: 'combo',
			sortField: 'advisorName',
		},
		{
			key: 'disabilityType',
			labelKey: 'database_view.leks.columns.disability_type',
			filterKey: 'disabilityType',
			filterType: 'combo',
			sortField: 'disabilityType',
		},
		{
			key: 'assignmentNumber',
			labelKey: 'database_view.leks.columns.assignment_number',
		},
		{ key: 'institutionAtStart', labelKey: 'database_view.assignments.columns.institution_at_start' },
		{ key: 'institutionAtDate', labelKey: 'database_view.assignments.columns.institution_at_date' },
		{ key: 'institutionAtEnd', labelKey: 'database_view.assignments.columns.institution_at_end' },
		{ key: 'schoolNumberAtStart', labelKey: 'database_view.assignments.columns.school_number_at_start' },
		{ key: 'schoolNumberAtDate', labelKey: 'database_view.assignments.columns.school_number_at_date' },
		{ key: 'schoolNumberAtEnd', labelKey: 'database_view.assignments.columns.school_number_at_end' },
		{
			key: 'socialSecurityStatusAtStart',
			labelKey: 'database_view.assignments.columns.social_security_status_at_start',
		},
		{ key: 'socialSecurityStatusAtDate', labelKey: 'database_view.assignments.columns.social_security_status_at_date' },
		{ key: 'socialSecurityStatusAtEnd', labelKey: 'database_view.assignments.columns.social_security_status_at_end' },
		{ key: 'disabilityTypeAtStart', labelKey: 'database_view.assignments.columns.disability_type_at_start' },
		{ key: 'disabilityTypeAtDate', labelKey: 'database_view.assignments.columns.disability_type_at_date' },
		{ key: 'disabilityTypeAtEnd', labelKey: 'database_view.assignments.columns.disability_type_at_end' },
		{ key: 'disabilityProofAtStart', labelKey: 'database_view.assignments.columns.disability_proof_at_start' },
		{ key: 'disabilityProofAtDate', labelKey: 'database_view.assignments.columns.disability_proof_at_date' },
		{ key: 'disabilityProofAtEnd', labelKey: 'database_view.assignments.columns.disability_proof_at_end' },
		{ key: 'gdbAtStart', labelKey: 'database_view.assignments.columns.gdb_at_start' },
		{ key: 'gdbAtDate', labelKey: 'database_view.assignments.columns.gdb_at_date' },
		{ key: 'gdbAtEnd', labelKey: 'database_view.assignments.columns.gdb_at_end' },
		{
			key: 'lastThreeContactDates',
			labelKey: 'database_view.assignments.columns.last_three_contact_dates',
			sortField: 'lastThreeContactDates',
		},
		{ key: 'contactTaskCount', labelKey: 'database_view.assignments.columns.contact_task_count' },
		{ key: 'impedimentsAtStart', labelKey: 'database_view.assignments.columns.impediments_at_start' },
		{ key: 'impedimentsAtEnd', labelKey: 'database_view.assignments.columns.impediments_at_end' },
	];

	private lastCaseGroupVisibility: boolean | undefined;
	private initializationSequence = 0;
	private readonly loadingCounter = signal(0);
	private readonly schema = signal<DatabaseViewSchema | null>(null);
	private readonly schemaReady = signal(false);
	private readonly requestSequence = signal(0);
	private readonly apiRows = signal<DatabaseViewLekResponse[]>([]);
	private readonly totalCount = signal(0);
	private readonly dynamicFilterOptions = signal<Record<FilterKey, DatabaseFilterOption[]>>({
		...EMPTY_FILTER_OPTIONS,
		phase: nameOptions([...DATABASE_PHASE_FILTER_OPTIONS]),
	});
	private readonly unavailableFilterSources = signal<ReadonlySet<string>>(new Set());
	private readonly preloadPending = signal(0);
	/** The newest list request; a new list request or a representation change aborts it. */
	private listAbort: AbortController | null = null;
	private lastRepresentationKey: string | null = null;
	private readonly initialPageReady = signal(false);

	public readonly selectedColumnIds = signal<string[]>([]);
	public readonly applyingColumns = signal(false);
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.environment.defaultPageSize);
	public readonly sortField = signal<LekSortField | null>('assistanceResult');
	public readonly sortDirection = signal<DatabaseSortDirection>('asc');
	public readonly activeSortField = signal<LekSortField>('assistanceResult');
	public readonly filterValues = signal<Record<FilterKey, string[]>>({ ...EMPTY_FILTER_VALUES });
	public readonly filterSearch = signal<Record<FilterKey, string>>({ ...EMPTY_FILTER_SEARCH });
	public readonly dateRanges = signal<Partial<Record<LekDateRangeFieldId, DatabaseDateRangeValue>>>({});
	/** Reference date (Stichtag); only sent while an atDate column is selected. */
	public readonly referenceDate = signal<Date | null>(null);
	public readonly referenceDateAvailable = computed(() => isReferenceDateAvailable(this.selectedColumnIds()));
	public readonly rangeStart = signal<Date | null>(null);
	public readonly rangeEnd = signal<Date | null>(null);
	public readonly rangePreset = signal<DatabaseRangePreset | null>(null);
	public readonly exporting = signal(false);

	public readonly showCaseGroup = computed(() => this.tenantFeatures.caseGroupEnabled());
	public columns: LekColumn[] = this.showCaseGroup()
		? this.allColumns
		: this.allColumns.filter((column) => column.key !== 'caseGroup');
	public displayedColumns: ColumnKey[] = this.columns.map((column) => column.key);
	public readonly requestPayload = computed(() =>
		buildDatabaseViewLekRequest(
			{
				rangeStart: formatDateToISOString(this.rangeStart()) ?? '',
				rangeEnd: formatDateToISOString(this.rangeEnd()) ?? '',
				filters: this.effectiveFilterValues(),
				filterSearch: this.effectiveFilterSearch(),
				dateRanges: this.queryDateRanges(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
			},
			this.schema(),
			this.selectedColumnIds(),
			this.referenceDate(),
		),
	);
	public readonly columnOptions = computed<DatabaseColumnOption[]>(() => {
		const schema = this.schema();
		if (!schema) return [];
		const showCaseGroup = this.showCaseGroup();
		return schema.columns.flatMap(({ id: schemaId }) => {
			const column = this.allColumns.find(
				(candidate) => (showCaseGroup || candidate.key !== 'caseGroup') && LEK_SCHEMA_IDS[candidate.key] === schemaId,
			);
			return column ? [{ schemaId, renderKey: column.key, label: this.transloco.translate(column.labelKey) }] : [];
		});
	});
	private readonly defaultColumnIds = computed(() => this.columnOptions().map((option) => option.schemaId));
	public readonly canExport = computed(() => this.schemaReady() && !this.isPageLoading());
	public readonly pageRows = computed(() =>
		this.initialPageReady() ? this.apiRows().map((x) => this.mapLekToRow(x)) : [],
	);
	public readonly totalItems = computed(() => (this.initialPageReady() ? this.totalCount() : 0));
	public readonly controlsLocked = computed(
		() => !this.schemaReady() || this.applyingColumns() || this.preloadPending() > 0,
	);
	public readonly columnSelectorLocked = computed(() => this.controlsLocked() || this.loadingCounter() > 0);
	public readonly isPageLoading = computed(() => this.loadingCounter() > 0 || this.controlsLocked());
	public readonly normalizedFilterOptions = computed(() => {
		const options = this.dynamicFilterOptions();
		const next = { ...EMPTY_FILTER_OPTIONS };
		for (const key of COMBO_FILTER_KEYS) {
			next[key] = sanitizeFilterOptionEntries(options[key] ?? []);
		}
		return next;
	});
	public readonly filterOptionValueMap = computed(() => {
		const options = this.normalizedFilterOptions();
		const next = { ...EMPTY_FILTER_VALUES };
		for (const key of FILTER_KEYS) {
			next[key] = filterOptionValues(options[key]);
		}
		return next;
	});
	public readonly filterOptionLabelMap = computed(() => {
		const options = this.normalizedFilterOptions();
		const next = createRecordFromKeys(FILTER_KEYS, () => ({}) as Record<string, string>);
		for (const key of FILTER_KEYS) {
			next[key] = filterOptionLabels(options[key]);
		}
		return next;
	});
	public readonly rangePresetButtonKey = computed(() => {
		const preset = this.rangePreset();
		return preset ? RANGE_PRESET_LABEL_KEYS[preset] : RANGE_PRESET_PLACEHOLDER_KEY;
	});
	public readonly selectedRangeLabel = computed(() =>
		this.dateFormat.formatPeriodRange(this.rangeStart(), this.rangeEnd()),
	);

	public constructor() {
		this.hydrateStateFromStorage();
		this.destroyRef.onDestroy(this.session.onInvalidate(() => this.withdrawForSessionEnd()));
		this.destroyRef.onDestroy(() => this.abortListRequest());
		effect(() => {
			const state = this.representation?.mode();
			const key = state ? `${state.active}|${state.representedUserId ?? ''}` : '';
			untracked(() => {
				if (this.lastRepresentationKey !== null && this.lastRepresentationKey !== key) this.abortListRequest();
				this.lastRepresentationKey = key;
			});
		});
		effect(() => {
			const showCaseGroup = this.showCaseGroup();
			const visibilityChanged =
				this.lastCaseGroupVisibility !== undefined && this.lastCaseGroupVisibility !== showCaseGroup;
			this.lastCaseGroupVisibility = showCaseGroup;
			this.columns = showCaseGroup ? this.allColumns : this.allColumns.filter((column) => column.key !== 'caseGroup');
			untracked(() => {
				// Column options are empty until the schema loads; reconciling earlier would drop the restored selection.
				if (this.schema()) {
					this.selectedColumnIds.update((selected) =>
						reconcileColumnIds(this.columnOptions(), selected, this.defaultColumnIds()),
					);
				}
				this.syncReferenceDate(this.selectedColumnIds());
				this.updateDisplayedColumns();
			});
			if (visibilityChanged) untracked(() => void this.loadLeks());
		});
		afterNextRender(() => {
			const initializationSequence = ++this.initializationSequence;
			void this.loadLekFilterOptions(initializationSequence);
			void this.loadLeks(initializationSequence);
		});
	}

	/** Drops shown rows, first-page state and in-flight responses when the session ends. */
	private withdrawForSessionEnd() {
		this.abortListRequest();
		this.apiRows.set([]);
		this.totalCount.set(0);
		this.initialPageReady.set(false);
		this.filterPanels.closeActive();
	}

	private abortListRequest() {
		this.requestSequence.update((sequence) => sequence + 1);
		this.listAbort?.abort();
		this.listAbort = null;
	}

	public async export() {
		if (this.exporting() || !this.canExport()) return;
		await runDatabaseCsvExport({
			exporting: this.exporting,
			fetchCsv: () => this.databaseViewApi.getLeksCsv(this.requestPayload()),
			filename: 'lek_liste.csv',
			translate: (key) => this.transloco.translate(key),
			notify: (notification) => this.ntfy.addNotification(notification),
		});
	}

	public async applyColumns(candidateIds: readonly string[]) {
		if (this.columnSelectorLocked()) return;
		const nextIds = reconcileColumnIds(this.columnOptions(), candidateIds, this.selectedColumnIds());
		if (!nextIds.length || areColumnIdsEqual(nextIds, this.selectedColumnIds())) return;
		const removedColumns = this.columns.filter((column) => !nextIds.includes(LEK_SCHEMA_IDS[column.key]));
		const nextDateRanges = Object.fromEntries(
			Object.entries(this.dateRanges()).filter(([fieldId]) => nextIds.includes(fieldId)),
		) as Partial<Record<LekDateRangeFieldId, DatabaseDateRangeValue>>;
		const {
			filterValues: nextFilterValues,
			filterSearch: nextFilterSearch,
			sortField: nextSortField,
			sortDirection: nextSortDirection,
			resetSort,
		} = resetStateForRemovedColumns(
			{
				filterValues: this.filterValues(),
				filterSearch: this.filterSearch(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
			},
			removedColumns.map((column) => ({ filterKey: column.filterKey, sortField: column.sortField })),
			'asc',
		);
		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.applyingColumns.set(true);
		try {
			const page = await this.databaseViewApi.getLeks(
				buildDatabaseViewLekRequest(
					{
						rangeStart: formatDateToISOString(this.rangeStart()) ?? '',
						rangeEnd: formatDateToISOString(this.rangeEnd()) ?? '',
						filters: this.effectiveFilterValues(nextFilterValues),
						filterSearch: this.effectiveFilterSearch(nextFilterSearch),
						dateRanges: this.queryDateRanges(nextDateRanges, nextIds),
						sortField: nextSortField,
						sortDirection: nextSortDirection,
					},
					this.schema(),
					nextIds,
					this.referenceDate(),
				),
				0,
				this.pageSize(),
			);
			if (sequence !== this.requestSequence()) return;
			this.selectedColumnIds.set([...nextIds]);
			this.syncReferenceDate(nextIds);
			this.filterValues.set(nextFilterValues);
			this.filterSearch.set(nextFilterSearch);
			this.dateRanges.set(nextDateRanges);
			this.sortField.set(nextSortField);
			this.sortDirection.set(nextSortDirection);
			if (resetSort) this.activeSortField.set('assistanceResult');
			this.updateDisplayedColumns();
			this.pageIndex.set(0);
			this.apiRows.set(page.entities);
			this.totalCount.set(page.totalEntities);
			this.initialPageReady.set(true);
			this.persistStateToStorage();
			this.selector()?.close();
		} catch {
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.columns.apply_error')));
		} finally {
			// Only one column request runs at a time (guarded above), so this request owns the flag.
			this.applyingColumns.set(false);
		}
	}

	public setSort(field: LekSortField, direction: DatabaseSortDirection) {
		if (this.controlsLocked()) return;
		this.sortField.set(field);
		this.sortDirection.set(direction);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public setActiveSortField(field: LekSortField) {
		if (this.controlsLocked()) return;
		this.activeSortField.set(field);
	}

	public isSortOptionActive(field: LekSortField, direction: DatabaseSortDirection) {
		return this.sortField() === field && this.sortDirection() === direction;
	}

	public sortIcon(field: LekSortField | null | undefined) {
		if (field !== this.sortField() || !field) return 'swap_vert';
		return this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	public sortLabel(direction: DatabaseSortDirection) {
		if (this.activeSortField() === 'lastThreeContactDates') {
			return direction === 'asc'
				? 'database_view.assignments.sort.date_ascending'
				: 'database_view.assignments.sort.date_descending';
		}
		return direction === 'asc' ? 'database_view.leks.sort.ascending' : 'database_view.leks.sort.descending';
	}

	public setRangeStart(value: Date | null) {
		if (this.controlsLocked()) return;
		this.rangePreset.set(null);
		this.rangeStart.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public setRangeEnd(value: Date | null) {
		if (this.controlsLocked()) return;
		this.rangePreset.set(null);
		this.rangeEnd.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public clearDateRange(event?: Event) {
		event?.stopPropagation();
		if (this.controlsLocked()) return;
		if (!this.rangeStart() && !this.rangeEnd()) return;
		this.rangePreset.set(null);
		this.rangeStart.set(null);
		this.rangeEnd.set(null);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public applyRangePreset(preset: DatabaseRangePreset) {
		if (this.controlsLocked()) return;
		const range = resolveDatabaseRangePreset(preset, new Date());
		this.rangeStart.set(range.start);
		this.rangeEnd.set(range.end);
		this.rangePreset.set(preset);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public setFilterSearch(key: FilterKey, value: string) {
		if (this.controlsLocked()) return;
		this.filterSearch.update((s) => ({ ...s, [key]: value }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public toggleFilterValue(key: FilterKey, value: string, checked: boolean) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => toggleFilterSelection(state, key, value, checked));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public applyMultiFilterSelection(
		key: FilterKey,
		values: readonly string[],
		filterPanel: Pick<DatabaseMultiSelectFilter, 'closePanel'>,
	) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => ({ ...state, [key]: [...values] }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		filterPanel.closePanel();
		void this.loadLeks();
	}

	public onMultiFilterPanelClosed(key: FilterKey) {
		this.filterSearch.update((state) => ({ ...state, [key]: '' }));
	}

	public filterOptions(key: FilterKey) {
		return this.filterOptionValueMap()[key];
	}

	public filterLabels(key: FilterKey) {
		return this.filterOptionLabelMap()[key];
	}

	public isFilterSourceUnavailable(key: FilterKey) {
		return this.unavailableFilterSources().has(key);
	}

	public dateRangeFieldId(column: LekColumn): LekDateRangeFieldId | null {
		return column.filterType === 'dateRange' ? (column.fieldId ?? null) : null;
	}

	public columnDateRange(fieldId: LekDateRangeFieldId): DatabaseDateRangeValue {
		return this.dateRanges()[fieldId] ?? EMPTY_DATE_RANGE;
	}

	public isDateRangeUnavailable(fieldId: LekDateRangeFieldId): boolean {
		const column = this.schema()?.columns.find((candidate) => candidate.id === fieldId);
		return !column?.filterOperations.includes('between');
	}

	public setColumnDateRange(fieldId: LekDateRangeFieldId, value: DatabaseDateRangeValue) {
		if (this.controlsLocked()) return;
		this.dateRanges.update((ranges) => {
			const next = { ...ranges };
			if (value.start || value.end) next[fieldId] = value;
			else delete next[fieldId];
			return next;
		});
		this.afterColumnDateRangeChange();
	}

	public clearColumnDateRange(fieldId: LekDateRangeFieldId) {
		if (this.controlsLocked() || !this.dateRanges()[fieldId]) return;
		this.dateRanges.update((ranges) => {
			const next = { ...ranges };
			delete next[fieldId];
			return next;
		});
		this.afterColumnDateRangeChange();
	}

	private afterColumnDateRangeChange() {
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public resetAll() {
		if (this.controlsLocked()) return;
		this.filterValues.set({ ...EMPTY_FILTER_VALUES });
		this.filterSearch.set({ ...EMPTY_FILTER_SEARCH });
		this.rangePreset.set(null);
		this.rangeStart.set(null);
		this.rangeEnd.set(null);
		this.dateRanges.set({});
		this.referenceDate.set(null);
		this.sortField.set('assistanceResult');
		this.sortDirection.set('asc');
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public handlePageEvent(event: PageEvent) {
		if (this.controlsLocked()) return;
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public getCellValue(row: LekRow, key: ColumnKey) {
		return String(row[key] ?? '-');
	}

	public hasClientRoute(row: LekRow) {
		return row.clientId > 0;
	}

	public clientRoute(row: LekRow) {
		return ['/desk/client', row.clientId];
	}

	public hasAssignmentRoute(row: LekRow) {
		return row.clientId > 0 && row.assignmentId > 0;
	}

	public assignmentRoute(row: LekRow) {
		return ['/desk/client', row.clientId, 'assignments', row.assignmentId];
	}

	public hasIfdRoute(row: LekRow) {
		return row.ifdId > 0;
	}

	public ifdRoute(row: LekRow) {
		return ['/desk/integration-service-office', row.ifdId];
	}

	public hasAdvisorRoute(row: LekRow) {
		return row.advisorGuid !== '' || row.advisorId > 0;
	}

	public advisorRoute(row: LekRow) {
		if (row.advisorGuid !== '') return ['/desk/user', row.advisorGuid];
		return ['/desk/user', row.advisorId];
	}

	private async loadLeks(initializationSequence?: number): Promise<'loaded' | 'stale' | 'failed'> {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.listAbort?.abort();
		const controller = new AbortController();
		this.listAbort = controller;
		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.loadingCounter.update((count) => count + 1);
		try {
			if (!this.schema()) {
				this.schema.set(
					await this.viewStorage.resolveSchema(
						TAB_ID,
						SCHEMA_VIEW,
						() => this.databaseViewApi.getLeksSchema(),
						bypassCache,
					),
				);
				this.selectedColumnIds.set(
					reconcileColumnIds(this.columnOptions(), this.selectedColumnIds(), this.defaultColumnIds()),
				);
				this.syncReferenceDate(this.selectedColumnIds());
				this.updateDisplayedColumns();
				this.schemaReady.set(true);
				this.persistStateToStorage();
			}
			const page = await this.databaseViewApi.getLeks(
				this.requestPayload(),
				this.pageIndex(),
				this.pageSize(),
				controller.signal,
			);
			if (sequence !== this.requestSequence()) return 'stale';
			if (initializationSequence !== undefined && initializationSequence !== this.initializationSequence)
				return 'stale';
			this.apiRows.set(page.entities);
			this.totalCount.set(page.totalEntities);
			this.initialPageReady.set(true);
			this.viewStorage.markTabRefreshed(TAB_ID);
			return 'loaded';
		} catch (error) {
			const isCurrent =
				sequence === this.requestSequence() &&
				(initializationSequence === undefined || initializationSequence === this.initializationSequence);
			if (!isCurrent || controller.signal.aborted) return 'stale';
			void this.errorService.handleError(error, true);
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.load_error')));
			return 'failed';
		} finally {
			if (this.listAbort === controller) this.listAbort = null;
			this.loadingCounter.update((count) => Math.max(0, count - 1));
		}
	}

	private async loadLekFilterOptions(initializationSequence: number) {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.preloadPending.update((count) => count + 1);
		try {
			const sources = await this.viewStorage.resolveSourceOptions(
				TAB_ID,
				LEK_FILTER_SOURCE_KEYS,
				(missing) => this.loadLekFilterSources(missing),
				bypassCache,
			);
			if (initializationSequence !== this.initializationSequence) return;
			this.unavailableFilterSources.set(new Set(LEK_FILTER_SOURCE_KEYS.filter((key) => sources[key] === null)));
			if (sources.advisorName === null) {
				this.ntfy.addNotification(
					createErrorNotification(this.transloco.translate('database_view.filters.user_options_error')),
				);
			}
			if (LEK_FILTER_SOURCE_KEYS.some((key) => key !== 'advisorName' && sources[key] === null)) {
				this.ntfy.addNotification(
					createErrorNotification(this.transloco.translate('database_view.leks.filter_options_error')),
				);
			}
			this.applyLekFilterOptions(sources);
		} finally {
			this.preloadPending.update((count) => Math.max(0, count - 1));
		}
	}

	private async loadLekFilterSources(
		missing: readonly LekFilterSourceKey[],
	): Promise<Record<LekFilterSourceKey, DatabaseFilterOption[] | null>> {
		const sharedKeys = missing.filter(
			(key): key is LekSharedFilterSourceKey => key !== 'categorySponsor' && key !== 'assistanceResult',
		);
		const [shared, categorySponsor, assistanceResult] = await Promise.all([
			sharedKeys.length > 0
				? loadDatabaseFilterSourceOptions(
						{
							categoriesApi: this.categoriesApi,
							legalFoundationApi: this.legalFoundationApi,
							databaseViewApi: this.databaseViewApi,
							disabilityTypeApi: this.disabilityTypeApi,
							isoService: this.isoService,
							userService: this.userService,
						},
						this.filterOptionsOffset,
						this.filterOptionsRange,
						sharedKeys,
					)
				: Promise.resolve({}),
			missing.includes('categorySponsor')
				? this.categoriesApi
						.getSponsorOptions(this.filterOptionsOffset, this.filterOptionsRange)
						.then(nameOptions)
						.catch(() => null)
				: null,
			missing.includes('assistanceResult')
				? this.categoriesApi
						.getCategoryAssistanceOptions(this.filterOptionsOffset, this.filterOptionsRange)
						.then(nameOptions)
						.catch(() => null)
				: null,
		]);
		return { ...shared, categorySponsor, assistanceResult } as Record<
			LekFilterSourceKey,
			DatabaseFilterOption[] | null
		>;
	}

	private applyLekFilterOptions(options: Partial<Record<FilterKey, DatabaseFilterOption[] | null>>) {
		const next = { ...this.dynamicFilterOptions() };
		for (const key of LEK_PRELOAD_FILTER_KEYS) {
			const values = options[key];
			if (values) next[key] = values;
		}
		this.dynamicFilterOptions.set(next);
	}

	private updateDisplayedColumns() {
		this.displayedColumns = this.selectedColumnIds().flatMap((schemaId) =>
			this.columns.filter((column) => LEK_SCHEMA_IDS[column.key] === schemaId).map((column) => column.key),
		);
	}

	private effectiveFilterValues(filterValues = this.filterValues()) {
		const filters = { ...filterValues };
		if (!this.showCaseGroup()) {
			filters.caseGroup = [];
		}
		if (filters.phase?.includes('P1')) {
			filters.phase = [...new Set([...filters.phase, 'P0'])];
		}
		return filters;
	}

	private effectiveFilterSearch(filterSearch = this.filterSearch()) {
		const search = { ...filterSearch };
		if (!this.showCaseGroup()) {
			search.caseGroup = '';
		}
		return search;
	}

	private mapLekToRow(lek: DatabaseViewLekResponse): LekRow {
		const startDate = this.dateFormat.formatResponseDate(lek.startDate);
		const endDate = this.dateFormat.formatResponseDate(lek.endDate);

		return {
			clientId: lek.clientId,
			assignmentId: lek.assignmentId,
			ifdId: lek.integrationServiceOfficeId,
			advisorId: lek.advisorId,
			advisorGuid: lek.advisorGuid,
			assistanceResult: lek.assistanceResult || '-',
			clientName: lek.clientName || '-',
			startDate: startDate || '-',
			endDate: endDate || '-',
			duration: String(lek.duration ?? ''),
			categorySponsor: lek.categorySponsor || '-',
			phase: this.mapPhaseForDisplay(lek.phase),
			typeOfCare: lek.typeOfCare || '-',
			startOfCare: this.dateFormat.formatResponseDate(lek.startOfCare) || '-',
			cancellationDate: this.dateFormat.formatResponseDate(lek.cancellationDate) || '-',
			caseGroup: lek.caseGroup || '-',
			legalFoundation: lek.legalFoundation || '-',
			initiator: lek.initiator || '-',
			ifd: lek.integrationServiceOffice || '-',
			service: lek.service || '-',
			advisor: lek.advisorName || '-',
			disabilityType: lek.disabilityType || '-',
			assignmentNumber: lek.assignmentNumber || '-',
			institutionAtStart: lek.institutionAtStart || '-',
			institutionAtDate: lek.institutionAtDate || '-',
			institutionAtEnd: lek.institutionAtEnd || '-',
			schoolNumberAtStart: lek.schoolNumberAtStart || '-',
			schoolNumberAtDate: lek.schoolNumberAtDate || '-',
			schoolNumberAtEnd: lek.schoolNumberAtEnd || '-',
			socialSecurityStatusAtStart: lek.socialSecurityStatusAtStart || '-',
			socialSecurityStatusAtDate: lek.socialSecurityStatusAtDate || '-',
			socialSecurityStatusAtEnd: lek.socialSecurityStatusAtEnd || '-',
			disabilityTypeAtStart: lek.disabilityTypeAtStart || '-',
			disabilityTypeAtDate: lek.disabilityTypeAtDate || '-',
			disabilityTypeAtEnd: lek.disabilityTypeAtEnd || '-',
			disabilityProofAtStart: lek.disabilityProofAtStart || '-',
			disabilityProofAtDate: lek.disabilityProofAtDate || '-',
			disabilityProofAtEnd: lek.disabilityProofAtEnd || '-',
			gdbAtStart: lek.gdbAtStart || '-',
			gdbAtDate: lek.gdbAtDate || '-',
			gdbAtEnd: lek.gdbAtEnd || '-',
			assignmentSponsor: lek.assignmentSponsor || '-',
			lastThreeContactDates: lek.lastThreeContactDates || '-',
			contactTaskCount: lek.contactTaskCount == null ? '-' : String(lek.contactTaskCount),
			impedimentsAtStart: lek.impedimentsAtStart.join(', ') || '-',
			impedimentsAtEnd: lek.impedimentsAtEnd.join(', ') || '-',
		};
	}

	private mapPhaseForDisplay(value?: string | null) {
		return value === 'P0' ? 'P1' : value || '-';
	}

	private isSortField(value: string): value is LekSortField {
		return this.columns.some((column) => column.sortField === value);
	}

	private hydrateStateFromStorage() {
		const savedPageSize = this.getSavedPageSize();
		if (savedPageSize) this.pageSize.set(savedPageSize);

		const parsed = this.viewStorage.readListState(TAB_ID, parseLekStoredState);
		if (!parsed) return;

		if (parsed.pageIndex != null) this.pageIndex.set(parsed.pageIndex);
		if (parsed.sortField === null) {
			this.sortField.set(null);
			this.activeSortField.set('assistanceResult');
		} else if (parsed.sortField && this.isSortField(parsed.sortField)) {
			this.sortField.set(parsed.sortField);
			this.activeSortField.set(parsed.sortField);
		}
		if (parsed.sortDirection === 'asc' || parsed.sortDirection === 'desc') {
			this.sortDirection.set(parsed.sortDirection);
		}
		const restoredStart = this.parseDateParam(parsed.rangeStart ?? null);
		const restoredEnd = this.parseDateParam(parsed.rangeEnd ?? null);
		this.rangeStart.set(restoredStart);
		this.rangeEnd.set(restoredEnd);
		this.rangePreset.set(
			isDatabaseRangePreset(parsed.rangePreset) && restoredStart && restoredEnd ? parsed.rangePreset : null,
		);

		const restoredFilterValues = { ...EMPTY_FILTER_VALUES };
		const restoredFilterSearch = { ...EMPTY_FILTER_SEARCH };
		for (const key of FILTER_KEYS) {
			restoredFilterValues[key] = parsed.filterValues?.[key] ?? [];
			restoredFilterSearch[key] = parsed.filterSearch?.[key] ?? '';
		}
		this.filterValues.set({
			...restoredFilterValues,
			advisorName: (parsed.filterValues?.advisorName ?? []).filter(isNumericFilterId),
		});
		this.filterValues.update((state) => ({
			...state,
			phase: [...new Set(state.phase.map((value) => (value === 'P0' ? 'P1' : value)))],
		}));
		this.filterSearch.set({
			...restoredFilterSearch,
			advisorName: '',
		});
		if (parsed.selectedColumns) this.selectedColumnIds.set(parsed.selectedColumns);
		if (parsed.dateRanges) this.dateRanges.set(this.parseStoredDateRanges(parsed.dateRanges, parsed.selectedColumns));
		const restoredReferenceDate = parseStoredDay(parsed.referenceDate ?? null);
		if (restoredReferenceDate && restoredReferenceDate <= new Date()) this.referenceDate.set(restoredReferenceDate);
		// Without stored columns the defaults apply later; the schema load syncs then.
		if (parsed.selectedColumns) this.syncReferenceDate(parsed.selectedColumns);
	}

	/** Clears the reference date when no atDate column is selected, so no hidden value survives. */
	private syncReferenceDate(selectedIds: readonly string[]) {
		if (!isReferenceDateAvailable(selectedIds) && this.referenceDate()) this.referenceDate.set(null);
	}

	public setReferenceDate(value: Date | null) {
		this.referenceDate.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadLeks();
	}

	public isReferenceDateColumn(column: LekColumn): boolean {
		return (REFERENCE_DATE_COLUMN_IDS as readonly string[]).includes(LEK_SCHEMA_IDS[column.key]);
	}

	private parseStoredDateRanges(
		stored: NonNullable<LekStoredState['dateRanges']>,
		selectedColumns?: string[],
	): Partial<Record<LekDateRangeFieldId, DatabaseDateRangeValue>> {
		const restored: Partial<Record<LekDateRangeFieldId, DatabaseDateRangeValue>> = {};
		for (const fieldId of LEK_DATE_RANGE_FIELD_IDS) {
			if (selectedColumns && !selectedColumns.includes(fieldId)) continue;
			const entry = stored[fieldId];
			const start = parseStoredDay(entry?.start);
			const end = parseStoredDay(entry?.end);
			if (!start && !end) continue;
			if (start && end && start > end) continue;
			restored[fieldId] = { start, end };
		}
		return restored;
	}

	private queryDateRanges(
		ranges: Partial<Record<string, DatabaseDateRangeValue>> = this.dateRanges(),
		selectedIds: readonly string[] = this.selectedColumnIds(),
	): Partial<Record<string, { start: string; end: string }>> {
		const query: Partial<Record<string, { start: string; end: string }>> = {};
		for (const [fieldId, value] of Object.entries(ranges)) {
			if (!value || !selectedIds.includes(fieldId)) continue;
			query[fieldId] = {
				start: formatDateToISOString(value.start) || '',
				end: formatDateToISOString(value.end) || '',
			};
		}
		return query;
	}

	private storedDateRanges(): NonNullable<LekStoredState['dateRanges']> {
		const stored: NonNullable<LekStoredState['dateRanges']> = {};
		for (const [fieldId, value] of Object.entries(this.dateRanges())) {
			if (!value) continue;
			stored[fieldId] = {
				start: formatDateToISOString(value.start) || null,
				end: formatDateToISOString(value.end) || null,
			};
		}
		return stored;
	}

	private parseDateParam(value: string | null) {
		if (!value) return null;
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	private persistStateToStorage() {
		const state = {
			pageIndex: this.pageIndex(),
			sortField: this.sortField(),
			sortDirection: this.sortDirection(),
			rangeStart: formatDateToISOString(this.rangeStart()) ?? null,
			rangeEnd: formatDateToISOString(this.rangeEnd()) ?? null,
			rangePreset: this.rangePreset(),
			filterValues: this.filterValues(),
			filterSearch: this.filterSearch(),
			selectedColumns: this.selectedColumnIds(),
			dateRanges: this.storedDateRanges(),
			referenceDate: this.storedReferenceDate(),
		};
		this.viewStorage.writeListState(TAB_ID, state);
	}

	private storedReferenceDate(): string | null {
		const value = this.referenceDate();
		return value ? (formatDateToISOString(new Date(value)) ?? null) : null;
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, PAGINATOR_PREFERENCE_KEY);
		if (!saved || !this.environment.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}
}
